/**
 * 有声画本录音 / AI TTS 上传与章节 lid 校验（与后端 utils/script-recording.js 约定一致，便于前后端同规则）
 */

/**
 * 音频上传成功（multipart 或 OSS complete）：`data` 常为 `{ id, audioId, files[], list[] }`，勿假定 `data` 为数组（有声录音接口 §12）。
 * MD5 去重时后端可能返回 `files[0].existingFile.id`（isDuplicate: true），无顶层 `id`。
 * @param {*} res 接口返回体（常用 `res.data` 为内层 data）
 * @returns {number|string|null}
 */
export function parseAudioUploadResponseId (res) {
  if (res == null) return null
  let d = res.data
  if (
    d == null &&
    typeof res === 'object' &&
    (res.id != null || res.audioId != null || Array.isArray(res.files) || Array.isArray(res.list))
  ) {
    d = res
  }
  if (d == null) return null
  if (typeof d === 'number') return d
  if (typeof d === 'object' && !Array.isArray(d)) {
    if (d.id != null) return d.id
    if (d.audioId != null) return d.audioId
    const pickFromUploadEntry = (entry) => {
      if (!entry || typeof entry !== 'object') return null
      if (entry.id != null) return entry.id
      if (entry.audioId != null) return entry.audioId
      const ex = entry.existingFile
      if (ex && typeof ex === 'object' && ex.id != null) return ex.id
      return null
    }
    if (Array.isArray(d.files) && d.files.length > 0) {
      const id0 = pickFromUploadEntry(d.files[0])
      if (id0 != null) return id0
    }
    if (Array.isArray(d.list) && d.list.length > 0) {
      const id0 = pickFromUploadEntry(d.list[0])
      if (id0 != null) return id0
    }
  }
  if (Array.isArray(d) && d[0]?.id != null) return d[0].id
  return null
}

/**
 * @param {string} description
 * @returns {Map<string, string>} key 小写，value 已 try decodeURIComponent
 */
export function parseRecordingDescriptionPairs (description) {
  const s = String(description || '')
  const out = new Map()
  for (const seg of s.split(';')) {
    const eq = seg.indexOf('=')
    if (eq <= 0) continue
    const k = seg.slice(0, eq).trim().toLowerCase()
    let v = seg.slice(eq + 1)
    try {
      v = decodeURIComponent(v)
    } catch {
      /* 保留原串 */
    }
    out.set(k, v)
  }
  return out
}

/** description 中是否已有非空 lid= */
export function recordingDescriptionHasLid (description) {
  const v = parseRecordingDescriptionPairs(description).get('lid')
  return v != null && String(v).trim() !== ''
}

/** 从 description 取出 lid 文本（trim 后），无则空串 */
export function extractLidFromRecordingDescription (description) {
  const v = parseRecordingDescriptionPairs(description).get('lid')
  return v != null ? String(v).trim() : ''
}

/**
 * 是否视为 AI/TTS 上传意图（与后端 isAiUploadIntent 对齐）
 * @param {{ description?: string, ttsAi?: string|null, audioSource?: string|null }} [fields]
 */
export function isAiUploadIntent (fields = {}) {
  const description = fields.description ?? ''
  const ttsAi = fields.ttsAi
  const audioSource = fields.audioSource
  const pairs = parseRecordingDescriptionPairs(description)
  const ttsFromDesc = String(pairs.get('ttsai') ?? '').trim().toLowerCase()
  const srcFromDesc = String(pairs.get('audiosource') ?? '').trim().toLowerCase()
  if (ttsFromDesc === '1' || ttsFromDesc === 'true') return true
  if (srcFromDesc === 'aitts' || srcFromDesc === 'ai_tts') return true

  const ttsTop = ttsAi != null ? String(ttsAi).trim().toLowerCase() : ''
  if (ttsTop === '1' || ttsTop === 'true') return true

  const srcTop = audioSource != null ? String(audioSource).trim().toLowerCase() : ''
  if (srcTop === 'aitts' || srcTop === 'ai_tts') return true
  return false
}

/**
 * AI/TTS 意图下保证 description 含 lid=；否则用顶层 lid 追加（encodeURIComponent）
 * @returns {string}
 */
export function ensureRecordingDescriptionHasLid (description, topLevelLid) {
  const d0 = String(description ?? '')
  if (recordingDescriptionHasLid(d0)) return d0
  const lid = String(topLevelLid ?? '').trim()
  if (!lid) {
    const e = new Error('lid_required_for_ai_upload')
    e.code = 'lid_required_for_ai_upload'
    throw e
  }
  const enc = encodeURIComponent(lid)
  if (!d0.trim()) return `lid=${enc}`
  return `${d0};lid=${enc}`
}

/**
 * 在提交 multipart 前调用：若为 AI/TTS 意图，补全 description 中的 lid，并同步顶层 lid 字段。
 * @param {FormData} formData
 */
export function applyAiUploadLidToFormData (formData) {
  if (!(formData instanceof FormData)) return
  const description = formData.get('description')
  const ttsAi = formData.has('ttsAi') ? formData.get('ttsAi') : undefined
  const audioSource = formData.has('audioSource') ? formData.get('audioSource') : undefined
  if (
    !isAiUploadIntent({
      description: description != null ? String(description) : '',
      ttsAi,
      audioSource
    })
  ) {
    return
  }
  const lidTopRaw = formData.get('lid')
  const lidTop = lidTopRaw != null ? String(lidTopRaw).trim() : ''
  const newDesc = ensureRecordingDescriptionHasLid(
    description != null ? String(description) : '',
    lidTop
  )
  formData.set('description', newDesc)
  const finalLid = extractLidFromRecordingDescription(newDesc)
  if (finalLid) formData.set('lid', finalLid)
}

/** 从章节正文收集 [lid:…] 内文本（trim，去重保序） */
export function collectLidsFromChapterContent (content) {
  const s = String(content || '')
  const re = /\[lid:([^\]]+)\]/g
  const out = []
  const seen = new Set()
  let m
  while ((m = re.exec(s)) !== null) {
    const id = String(m[1] || '').trim()
    if (!id || seen.has(id)) continue
    seen.add(id)
    out.push(id)
  }
  return out
}

/**
 * @param {string} content 章节正文
 * @param {string[]} lids 请求的 lid 列表
 * @returns {{ byLid: Record<string, boolean>, present: string[], missing: string[] }}
 */
export function mapRequestedLidsExistenceInContent (content, lids) {
  const presentSet = new Set(collectLidsFromChapterContent(content))
  /** @type {Record<string, boolean>} */
  const byLid = {}
  const present = []
  const missing = []
  for (const raw of lids || []) {
    const key = String(raw ?? '').trim()
    if (!key) continue
    const ok = presentSet.has(key)
    byLid[key] = ok
    if (ok) present.push(key)
    else missing.push(key)
  }
  return { byLid, present, missing }
}

/**
 * 从 GET /api/audio/list 的响应推断「句 lid → 成品音频 id」（audiofiles.id）。
 * 依赖上传时 description 中的 `lid=`（AI 有声与真人录音均会带）；同一 lid 多条时取较大 id（通常较新）。
 * 若传入 `chapterId`，只统计 description 中 `chapter=` / `chapter_id=` 与该章节一致的条目，避免多章复用同一 lid 时串章。
 * 老数据若 description 未带 chapter，在此过滤下不会进入映射（可依赖章节 GET 的 lid 映射或正文 [audioId]）。
 * @param {*} res list 接口响应
 * @param {string|number|null} [workId] 传入时只统计该作品下的条目
 * @param {string|number|null} [chapterId] 传入时只统计该章节（与 description 中 chapter 一致）
 * @returns {Record<string, number>}
 */
export function lidToAudioIdMapFromAudioListResponse (res, workId, chapterId) {
  const raw = res?.data?.items ?? res?.data?.list ?? (Array.isArray(res?.data) ? res.data : [])
  const list = Array.isArray(raw) ? raw : []
  const wid = workId != null && workId !== '' ? String(workId) : ''
  const restrictChapter = chapterId != null && String(chapterId).trim() !== ''
  const chTarget = restrictChapter ? String(chapterId).trim() : ''
  /** @type {Record<string, number>} */
  const out = {}
  for (const item of list) {
    if (!item || typeof item !== 'object') continue
    if (wid) {
      const w = item.workid ?? item.workId ?? item.teamId ?? item.teamid
      if (w != null && String(w) !== wid) continue
    }
    const desc = item.description != null ? String(item.description) : ''
    if (restrictChapter) {
      const pairs = parseRecordingDescriptionPairs(desc)
      const chRaw = pairs.get('chapter') ?? pairs.get('chapter_id')
      const chStr = chRaw != null ? String(chRaw).trim() : ''
      if (chStr === '' || chStr !== chTarget) continue
    }
    const lid = extractLidFromRecordingDescription(desc)
    if (!lid) continue
    const idRaw = item.id ?? item.audioId
    const n = Number(idRaw)
    if (!Number.isFinite(n) || n <= 0) continue
    const prev = out[lid]
    if (prev == null || n > prev) out[lid] = n
  }
  return out
}
